import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { Card, Chip, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import usePlanData from "@/hooks/data/use-plans-data";
import usePopOver from "@/hooks/use-popover";
import ICustomerSalesProduct from "@/interfaces/customer-sales-product.interfaces";
import { CustomerMessages } from "@/messages/customer/customer.messages";
import { PLAN_MANAGEMENT_MESSAGES } from "@/messages/customer/plan-management.messages";
import { CustomerSalesProductStatus } from "@/types/customer-sales-product.types";

import PlanInfoField from "../PlanInfoField";
import { PlanPopover } from "../PlanPopover";
import PlanManagementHeader from "../PlanManagementHeader";

type Props = {
  plan: ICustomerSalesProduct;
  messages: CustomerMessages;
  disableNewPlan: boolean;
  onCancelPlan: (plan: ICustomerSalesProduct) => void;
};

function getStatusChipColor(status: CustomerSalesProductStatus): "success" | "warning" | "default" {
  if (status === CustomerSalesProductStatus.Active) return "success";
  if (status === CustomerSalesProductStatus.HoldDuePaymentIssue) return "warning";
  return "default";
}

const sectionBoxSx = {
  bgcolor: "grey.50",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "grey.200",
  p: 2,
  height: "100%",
} as const;

function SectionTitle({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <MDBox display="flex" alignItems="center" gap={0.75} mb={1.25}>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          bgcolor: "white",
          border: "1px solid",
          borderColor: "grey.200",
          color: "dark.main",
        }}
      >
        {icon}
      </MDBox>
      <MDTypography variant="body2" fontWeight="bold" color="dark">
        {children}
      </MDTypography>
    </MDBox>
  );
}

export function PlanManagementCards({ plan, messages, disableNewPlan, onCancelPlan }: Props) {
  const planData = usePlanData({ plan, messages });
  const popover = usePopOver<ICustomerSalesProduct>();
  const { openPopover, anchorEl, closePopover, isOpen, selected } = popover;

  const endDateValue = plan.endDate ? planData.formattedEndDate : messages.activeContractLabel;

  return (
    <>
      <PlanManagementHeader messages={messages} disableNewPlan={disableNewPlan} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ overflow: "hidden", height: "100%" }}>
            <MDBox p={2.5} sx={sectionBoxSx}>
              <SectionTitle icon={<ArticleOutlinedIcon sx={{ fontSize: 16 }} />}>
                {PLAN_MANAGEMENT_MESSAGES.planDataCardTitle}
              </SectionTitle>
              <PlanInfoField
                label={messages.productColumn}
                value={planData.salesProduct?.name || "-"}
              />
              <PlanInfoField
                label={messages.planColumn}
                value={planData.salesProductPrice?.planName || "-"}
              />
              <PlanInfoField
                label={messages.statusColumn}
                value={
                  <Chip
                    label={planData.statusLabel}
                    size="small"
                    color={getStatusChipColor(plan.status)}
                    variant="outlined"
                  />
                }
              />
              <PlanInfoField label={messages.contractDateColumn} value={planData.formattedStartDate} />
              <PlanInfoField label={messages.endDateColumn} value={endDateValue} />
              <PlanInfoField
                label={messages.renewalDateColumn}
                value={planData.formattedNextPriceUpdate}
              />
              <MDBox display="flex" justifyContent="flex-end" mt={1}>
                <IconButton
                  aria-label="Opções do plano"
                  size="small"
                  sx={{ bgcolor: "white", border: "1px solid", borderColor: "grey.200" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openPopover(e, plan);
                  }}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ overflow: "hidden", height: "100%" }}>
            <MDBox p={2.5} sx={sectionBoxSx}>
              <SectionTitle icon={<PaymentsOutlinedIcon sx={{ fontSize: 16 }} />}>
                {PLAN_MANAGEMENT_MESSAGES.billingDetailsCardTitle}
              </SectionTitle>
              <PlanInfoField
                label={PLAN_MANAGEMENT_MESSAGES.nextMonthlyPaymentLabel}
                value={planData.formattedNextMonthlyPayment}
              />
              <PlanInfoField label={PLAN_MANAGEMENT_MESSAGES.dueDayLabel} value={planData.formattedDueDay} />
              <PlanInfoField
                label={PLAN_MANAGEMENT_MESSAGES.fixedMonthlyFeeTypeLabel}
                value={planData.fixedMonthlyFeeTypeLabel}
              />
              <PlanInfoField
                label={messages.fixedMonthlyFeeColumn}
                value={planData.formattedFixedMonthlyFee}
              />
              <MDBox mt={2}>
                <SectionTitle icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />}>
                  {PLAN_MANAGEMENT_MESSAGES.feesSectionTitle}
                </SectionTitle>
                <Grid container spacing={1.5} columnSpacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <PlanInfoField
                      label={messages.pricePerEventColumn}
                      value={planData.formattedPricePerEvent}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <PlanInfoField
                      label={messages.eventsIncludedMonthlyFeeColumn}
                      value={planData.formattedEventsIncludedMonthlyFee}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <PlanInfoField
                      label={messages.feePerSalesConversionColumn}
                      value={planData.formattedFeePerSalesConvertion}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <PlanInfoField
                      label={messages.fixedFeePerSalesConvertionColumn}
                      value={planData.formattedFixedFeePerSalesConvertion}
                    />
                  </Grid>
                </Grid>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {selected && (
        <PlanPopover
          isOpen={isOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          messages={messages}
          selected={selected}
          onCancelPlan={onCancelPlan}
        />
      )}
    </>
  );
}

export default PlanManagementCards;
